import { THEME_TAG_COLOR_CLASS_MAP } from '@terminal/modules/reflexivity-graph/kg-insight-constants';
import { ThemeLogoIcon } from '@terminal/views/theme-overview/components/theme-logo-icon/ThemeLogoIcon';
import type { EarningsTopic } from '@terminal/views/theme-overview/tab-content/theme-earnings/earnings-reporting-summary/types';
import {
  type SurpriseStat,
  toGuidanceTableKeyStat,
  toMedianKeyStat,
  toSurpriseKeyStat,
} from '@terminal/views/theme-overview/tab-content/theme-earnings/theme-key-stats/utils';
import type { GuidanceStat } from '@terminal/views/theme-overview/tab-content/theme-earnings/utils';
import { TopRankIcon } from '@terminal/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './IndustryThemesTable.module.scss';
import { KeyStatsCell } from './KeyStatsCell';
import { Placeholder } from './top-competitors/TopCompetitorCells';

export type IndustryThemeRowData = {
  themeId: string;
  rank: number;
  name: string;
  logoUrl?: string;
  reportedCount: number;
  totalCount: number;
  epsStat?: SurpriseStat;
  revenueStat?: SurpriseStat;
  guidanceStat?: GuidanceStat;
  medianT1?: number;
  medianT2?: number;
  leadingDriver?: EarningsTopic;
  isLoading: boolean;
};

export type IndustryThemesTableColumn = {
  id: string;
  header: string;
  alignment?: 'left' | 'right';
  cell: (row: IndustryThemeRowData) => ReactNode;
};

export const useIndustryThemesColumns = (): IndustryThemesTableColumn[] => {
  const { t } = useTranslation(['earnings']);

  return [
    {
      id: 'rank',
      header: t('earnings:peerComparison.industryThemes.rank'),
      cell: (row: IndustryThemeRowData) => <TopRankIcon rank={row.rank} />,
    },
    {
      id: 'theme',
      header: t('earnings:peerComparison.industryThemes.theme'),
      cell: (row: IndustryThemeRowData) => (
        <span className={styles.themeCell}>
          <ThemeLogoIcon
            name={row.name}
            logo_url={row.logoUrl}
            type="industry"
            size={20}
            fallbackIcon={
              <div className={styles.themeLogoFallback}>
                {row.name.charAt(0)}
              </div>
            }
          />
          <span className={styles.themeName}>{row.name}</span>
        </span>
      ),
    },
    {
      id: 'companiesReported',
      header: t('earnings:peerComparison.industryThemes.companiesReported'),
      alignment: 'right',
      cell: (row: IndustryThemeRowData) => (
        <span className={styles.companiesReported}>
          <span className={styles.reportedCount}>{row.reportedCount}</span>
          <span className={styles.totalCount}>/{row.totalCount}</span>
        </span>
      ),
    },
    {
      id: 'eps',
      header: t('earnings:peerComparison.industryThemes.eps'),
      alignment: 'left',
      cell: (row: IndustryThemeRowData) => (
        <KeyStatsCell
          keyStat={{
            ...toSurpriseKeyStat('eps', row.epsStat, 0),
            secondaryText: undefined,
          }}
        />
      ),
    },
    {
      id: 'revenue',
      header: t('earnings:peerComparison.industryThemes.revenue'),
      alignment: 'right',
      cell: (row: IndustryThemeRowData) => (
        <KeyStatsCell
          keyStat={{
            ...toSurpriseKeyStat('revenue', row.revenueStat, 0),
            secondaryText: undefined,
          }}
        />
      ),
    },
    {
      id: 'guidance',
      header: t('earnings:peerComparison.industryThemes.guidance'),
      alignment: 'left',
      cell: (row: IndustryThemeRowData) => (
        <KeyStatsCell keyStat={toGuidanceTableKeyStat(row.guidanceStat)} />
      ),
    },
    {
      id: 'peersMedianT1',
      header: t('earnings:peerComparison.industryThemes.peersMedianT1'),
      alignment: 'right',
      cell: (row: IndustryThemeRowData) => (
        <KeyStatsCell
          keyStat={toMedianKeyStat('peersMedianT1', row.medianT1)}
        />
      ),
    },
    {
      id: 'peersMedianT2',
      header: t('earnings:peerComparison.industryThemes.peersMedianT2'),
      alignment: 'right',
      cell: (row: IndustryThemeRowData) => (
        <KeyStatsCell
          keyStat={toMedianKeyStat('peersMedianT2', row.medianT2)}
        />
      ),
    },
    {
      id: 'leadingThemeDriver',
      header: t('earnings:peerComparison.industryThemes.leadingThemeDriver'),
      alignment: 'right',
      cell: (row: IndustryThemeRowData) =>
        row.leadingDriver?.label ? (
          <span
            className={THEME_TAG_COLOR_CLASS_MAP[row.leadingDriver.themeType]}
          >
            {row.leadingDriver.label}
          </span>
        ) : (
          <Placeholder />
        ),
    },
  ];
};
